"use client";

import { datadogRum } from "@datadog/browser-rum";
import { useEffect } from "react";
import packageJson from "../../package.json";
import { RemoteConfigEnum } from "@/constants/constants";
import { fetchConfigAsNumber, isTvEnv } from "@/utils/common.helpers";
import { useUserContext } from "@/context/UserProvider";
import { setRumUser, clearRumUser } from "@/lib/datadog-rum";

// Initialize RUM once at module level
let rumInitialized = false;

const initializeRum = async () => {
  if (rumInitialized) return;

  try {
    let env = "preview";
    if (process.env.NODE_ENV === "development") {
      env = "dev";
    } else if (process.env.NEXT_PUBLIC_IS_PRODUCTION === "true") {
      env = isTvEnv() ? "tv-prod" : "prod";
    }

    // Get session sampling rate from Firebase Remote Config
    const sessionSampleRate = isTvEnv()
      ? 100
      : await fetchConfigAsNumber(RemoteConfigEnum.DD_SAMPLING_RATE);

    // Get session replay sampling rate from Firebase Remote Config
    const sessionReplaySampleRate = await fetchConfigAsNumber(
      RemoteConfigEnum.DD_SESSION_REPLAY_RATE,
    );

    // Get RUM credentials from environment variables
    const applicationId = process.env.NEXT_PUBLIC_DD_RUM_APPLICATION_ID;
    const clientToken = process.env.NEXT_PUBLIC_DD_RUM_CLIENT_TOKEN;

    if (!applicationId || !clientToken) {
      console.warn(
        "DataDog RUM credentials not found. Skipping RUM initialization.",
      );
      return;
    }

    datadogRum.init({
      applicationId,
      clientToken,
      site: "datadoghq.com",
      service: "stage-web",
      env,
      version: packageJson.version,
      sessionSampleRate,
      sessionReplaySampleRate, // Enabled with Remote Config control (default: 20%)
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: "mask-user-input", // Masks sensitive user input
    });

    rumInitialized = true;
  } catch (error) {
    console.error("❌ Failed to initialize DataDog RUM:", error);
  }
};

// Initialize RUM immediately
initializeRum();

export const DatadogInit = () => {
  const { user, isLoggedIn } = useUserContext();

  // Update user context when authentication state changes
  useEffect(() => {
    if (isLoggedIn && user.userId) {
      setRumUser(user.userId, undefined, user.mobileNumber);
    } else {
      clearRumUser();
    }
  }, [isLoggedIn, user.userId, user.mobileNumber]);

  return null;
};
